import { mediaBaseURL, surveyBaseURL, userBaseURL } from "@/constants";
import type { Property } from "@/features/survey/types";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  getInteractiveToken,
  getSurveyGroupId,
  getSurveyId,
} from "./interactiveSurveyAuth";

// Enhanced factory to create a fetchBaseQuery with interactive token support
const createBaseQuery = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      // Set Accept header
      headers.set("Accept", "application/json, text/plain, */*");
      // Set sec-fetch-site header
      headers.set("sec-fetch-site", "same-origin");

      // Check for interactive survey token
      const interactiveToken = getInteractiveToken();
      if (interactiveToken) {
        headers.set("interactiveToken", interactiveToken);
      }

      // AccessTokenValidationFilter only recognizes the "surveyId" header for
      // its interactiveToken fallback check, so survey-group requests must
      // send the surveyGroupId under that same header name.
      const surveyId = getSurveyId() || getSurveyGroupId();
      if (surveyId) {
        headers.set("surveyId", surveyId);
      }

      return headers;
    },
  });

// Create base queries for each API domain
const surveyBaseQuery = createBaseQuery(surveyBaseURL);
const userBaseQuery = createBaseQuery(userBaseURL);
const mediaBaseQuery = createBaseQuery(mediaBaseURL);

// Create specialized base query for interactive surveys
export const interactiveSurveyBaseQuery = createBaseQuery(surveyBaseURL);

// Export simple API services
export const surveyApi = createApi({
  reducerPath: "surveyApi",
  baseQuery: surveyBaseQuery,
  endpoints: () => ({}),
});

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: userBaseQuery,
  endpoints: () => ({}),
});

export const mediaApi = createApi({
  reducerPath: "mediaApi",
  baseQuery: mediaBaseQuery,
  endpoints: () => ({}),
});

export const interactiveSurveyApi = createApi({
  reducerPath: "interactiveSurveyApi",
  baseQuery: interactiveSurveyBaseQuery,
  tagTypes: ["Property"],
  endpoints: (builder) => ({
    updatePropertyShortlist: builder.mutation<
      Property,
      { propertyId: string; isClientShortlist: boolean }
    >({
      query: ({ propertyId, isClientShortlist }) => ({
        url: "property",
        method: "PATCH",
        body: {
          propertyId,
          isClientShortlist,
        },
      }),
      invalidatesTags: ["Property"],
    }),
  }),
});

// Export hooks for use in components
export const { useUpdatePropertyShortlistMutation } = interactiveSurveyApi;
